import React from 'react'

import { AppTheme } from '@percept/mui'

import { OrgUnitBenchmarkDatum, ProviderBenchmarks } from '@percept/hooks'

import { flatten, get, sortBy, uniq } from 'lodash-es'

import { vodafoneMarkets } from 'vodafoneMarkets'

import { MarketDisplayLabel } from 'components/MarketDisplay'

import { providerLabelMap, reportProvidersV2 } from '@percept/constants'

import { getSegmentLabel, vodafoneMarketsById } from './lib'

import { ReportProviderV2 } from '@percept/types'

import { BenchmarkingTableRow } from './typings'

import { BenchmarkDatum } from './BenchmarkingQuadrantChart'

import { ScaleLinear, ScaleOrdinal } from 'd3-scale'


export const getDatumKey = (datum: Pick<BenchmarkDatum, 'org_unit_id' | 'provider' | 'segment'>): string => (
  [datum.org_unit_id, datum.provider, datum.segment].join('|')
)

export const getDatumLabel = (datum: Pick<BenchmarkDatum, | 'provider' | 'segment'>): string => (
  [providerLabelMap[datum.provider], datum.segment].join(' - ')
)


export const segmentExclusionMap: Partial<Record<ReportProviderV2, string[]>> = {
  GOOGLE_ADS: ['OPEN_WEB_DISPLAY', 'OPEN_WEB_VIDEO'],
}


const providerSegments: {provider: ReportProviderV2, segments: string[]}[] = [
  {
    provider: 'GOOGLE_ADS',
    segments: [
      'SEARCH',
      'OPEN_WEB_DISPLAY',
      'OPEN_WEB_VIDEO',
      'YOUTUBE',
    ],
  },
  {
    provider: 'FACEBOOK',
    segments: [
      'FACEBOOK',
      'INSTAGRAM',
    ],
  },
  {
    provider: 'ADFORM',
    segments: [
      'OPEN_WEB_DISPLAY',
    ],
  },
  {
    provider: 'DV360',
    segments: [
      'OPEN_WEB_DISPLAY',
      'OPEN_WEB_VIDEO',
      'YOUTUBE',
    ],
  },
  {
    provider: 'TIKTOK',
    segments: [
      'TIKTOK',
    ],
  },
]


const allSegmentKeys = providerSegments.reduce( (acc, { provider, segments }) => {
  const segmentExclusions = segmentExclusionMap[provider]
  for( const segment of segments ){
    if( !segmentExclusions || !segmentExclusions.includes(segment) ){
      acc.push(getDatumLabel({ provider, segment: getSegmentLabel(segment) }))
    }
  }
  return acc
}, [] as string[])



export const makeSegmentColourScale = (appTheme: AppTheme): ScaleOrdinal<string, string, never> => (
  appTheme.chart.getOrdinalColourScale(allSegmentKeys)
  // appTheme.chart.getInformationalColourScale([0, segments.length])
)

export const getBenchmarkChartData = (
  data: ProviderBenchmarks[],
  appTheme: AppTheme,
): BenchmarkDatum[] => {
  const chartData = sortBy(
    flatten(
      flatten(
        data.map( d =>
          d.results.map( b =>
            b.results.map( r => ({
              org_unit_id: r.org_unit_id,
              provider: d.provider,
              segment: getSegmentLabel(b.provider_segment.segment),
              effectiveness: r.effectiveness.relative_change,
              outcomes: r.outcomes.change,
            }))
          )
        )
      )
    ),
    [
      (row): number => vodafoneMarkets.findIndex( m => m.id === row.org_unit_id ),
      (row): number => reportProvidersV2.indexOf(row.provider),
      (row): string => row.segment,
    ]
  )

  const infoColourScale = makeSegmentColourScale(appTheme)

  return chartData.map( d => ({
    ...d,
    color: infoColourScale(getDatumLabel(d)),
  }))
}


const benchmarkDatumToTableRow = (
  benchmarkDatum: OrgUnitBenchmarkDatum,
  segmentParams: {
    provider: ReportProviderV2
    segment_label: string
  },
  singleMarketView: boolean
): BenchmarkingTableRow => ({
  org_unit_id: benchmarkDatum.org_unit_id,
  ...segmentParams,
  label: (
    singleMarketView ?
      <>
        {providerLabelMap[segmentParams.provider]}
        <span style={{marginLeft: 6, marginRight: 6}}>-</span>
        {getSegmentLabel(segmentParams.segment_label)}
      </> :
      vodafoneMarketsById[benchmarkDatum.org_unit_id] ?
        <MarketDisplayLabel {...vodafoneMarketsById[benchmarkDatum.org_unit_id]} /> :
        null
  ),
  effectiveness_metric: benchmarkDatum.effectiveness.rate_type,
  effectiveness_competitor: benchmarkDatum.effectiveness.competitor_group.change,
  effectiveness_market: benchmarkDatum.effectiveness.org_activity.change,
  effectiveness_change: benchmarkDatum.effectiveness.relative_change,
  cvr_previous: benchmarkDatum.outcomes.prior_rate,
  cvr_current: benchmarkDatum.outcomes.current_rate,
  cvr_change: benchmarkDatum.outcomes.change,
})


export const getBenchmarkingTableRows = (
  providerBenchmarks: ProviderBenchmarks[],
  singleMarketView = false,
): {
  rows: BenchmarkingTableRow[]
  metrics: string[]
 } => {
  const segmentLabelIndices: Record<string, number> = {}
  const metrics = uniq(
    flatten(
      flatten(
        providerBenchmarks.map( r => r.results.map( b =>
          b.results.map( r => get(r.effectiveness, 'rate_type') || '' )
        ))
      )
    )
  ).filter(Boolean)
  const rows = flatten(
    providerBenchmarks.map( p => p.results.reduce( (acc, r, i) => {
      const rowSegment = r.provider_segment.segment
      segmentLabelIndices[rowSegment] = i
      r.results.forEach( result => {
        acc.push(
          benchmarkDatumToTableRow(
            result, { segment_label: rowSegment, provider: p.provider }, singleMarketView
          )
        )
      })
      return acc
    }, [] as BenchmarkingTableRow[])
    )
  )
  return {
    rows: sortBy(
      rows,
      [
        (row): number => vodafoneMarkets.findIndex( m => m.id === row.org_unit_id ),
        (row): number => reportProvidersV2.indexOf(row.provider),
        (row): number => segmentLabelIndices[row.segment_label] || -1,
      ]
    ),
    metrics,
  }
}
